import { BrowserModule } from '@angular/platform-browser';
import { NgModule, Injector, inject, provideAppInitializer } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DatePipe } from '@angular/common';
import {
	HttpClientModule,
	HTTP_INTERCEPTORS
} from '@angular/common/http';
import { ServiceWorkerModule } from '@angular/service-worker';
import { Constants } from './constants';
import { environment } from '../environments/environment';

// Config Initializer
import { ConfigInitializer } from './config-initializer';
import { isChunkLoadError, isReloadPending } from './chunk-load-recovery';

// External
import * as Sentry from '@sentry/browser';
import { CookieService } from 'ngx-cookie-service';

// Routing
import { AppRoutingModule } from './app-routing.module';

// Modules
import { SharedModule } from './shared/shared.module';
import { AngularSvgIconModule } from 'angular-svg-icon';

// Components
import { AppComponent } from './app.component';

// Services
import { ConfigService } from './config.service';

// Auth Interceptors
import { AuthenticationInterceptor } from 'app/private/_core/_interceptors/authentication.interceptor';
import { AuthorizationInterceptor } from 'app/private/_core/_interceptors/authorization.interceptor';

// Sentry implementation to catch app crashes / errors
Sentry.init({
	dsn: 'https://8eca4f36344a40a7a643e416582a7d31@sentry.io/165354',
	release: Constants.CONSTANT_WEB_VERSION.split('Web/')[1] ?? Constants.CONSTANT_WEB_VERSION,
	environment: environment.name === 'prod' ? 'production' : environment.name,
	beforeSend: event => {
		if (!isChunkLoadError(event.exception?.values?.[0]?.value)) {
			return event;
		}

		if (isReloadPending()) {
			return null;
		}
		// prevent Sentry from creating a new issue for each chunk load error
		event.fingerprint = ['chunk-load-error'];
		return event;
	},
	ignoreErrors: [
		'INTERNAL ASSERTION FAILED', // ignore Firebase non-errors coming from Arena sdk
		'Non-Error exception captured with keys: error, headers, message, name, ok', // ignore Firebase non-errors coming from Arena sdk
		'Non-Error exception captured with keys: qa', // ignore ISH errors
		`Can't find variable: _ADB`, // ignore ISH errors
		'_ADB is not defined', // ignore ISH errors
		'Non-Error exception captured', // general ignore for non-errors
		'Non-Error promise rejection captured', // general ignore for non-errors
	]
});

@NgModule({
	declarations: [AppComponent],
	imports: [
		BrowserModule,
		AppRoutingModule,
		BrowserAnimationsModule,
		HttpClientModule,
		ServiceWorkerModule.register('/OneSignalSDKWorker.js', {
			enabled: environment.production
		}),
		SharedModule,
		AngularSvgIconModule.forRoot(),
	],
	providers: [
		DatePipe,
		ConfigService,
		CookieService,
		provideAppInitializer(() => {
        const initializerFn = (ConfigInitializer)(inject(Injector), inject(ConfigService));
        return initializerFn();
      }),
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthenticationInterceptor,
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthorizationInterceptor,
			multi: true
		}
	],
	bootstrap: [AppComponent]
})
export class AppModule {}
